import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';

const GA_ID = 'G-GR03N8YNZ7';
function usePageTracking() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag !== 'function') return;
    window.gtag('config', GA_ID, { page_path: location.pathname + location.search });
  }, [location]);
}
import Header     from './components/Header';
import Sidebar    from './components/Sidebar';
import SearchBar  from './components/SearchBar';
import StatsBar   from './components/StatsBar';
import TempleGrid from './components/TempleGrid';
import MapView    from './components/MapView';
import NearbyView from './components/NearbyView';
import SacredMarkersInfo from './components/SacredMarkersInfo';
import TempleOfDay from './components/TempleOfDay';
import IndiaMap from './components/IndiaMap';
import { CIRCUITS } from './data/circuits';
import { useTempleData } from './hooks/useTempleData';
import { useCircuitData } from './hooks/useCircuitData';
import { filterTemples, getCities, getDistricts, getStats } from './utils/filter';
import './App.css';

function AppShell() {
  usePageTracking();
  const { stateId, circuitId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [lang, setLang]           = useState('en');
  const [states, setStates]       = useState([]);
  const [search, setSearch]       = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);

  const { stateData, loading, error, loadState, clearState }             = useTempleData();
  const { circuitTemples, loading: circuitLoading, error: circuitError,
          loadCircuit, clearCircuit }                                     = useCircuitData();

  // URL-driven state
  const view        = searchParams.get('view') || 'grid';
  const activeCity  = searchParams.get('city')  || 'all';
  const activeDeity = searchParams.get('deity') || 'all';
  const faithRaw    = searchParams.get('faith');
  const faithSet    = useMemo(
    () => faithRaw ? new Set(faithRaw.split(',').filter(Boolean)) : new Set(['hindu','buddhist','jain']),
    [faithRaw]
  );

  const activeState   = stateId   ? (states.find(s => s.id === stateId)   || null) : null;
  const activeCircuit = circuitId ? (CIRCUITS.find(c => c.id === circuitId) || null) : null;
  const isHome        = !stateId && !circuitId;
  const isHinduOnly   = faithSet.size === 1 && faithSet.has('hindu');

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/data/states_index.json`)
      .then(r => r.json()).then(setStates).catch(console.error);
  }, []);

  useEffect(() => {
    if (!stateId || states.length === 0) return;
    const s = states.find(st => st.id === stateId);
    if (s) { loadState(s.id, s.file); setSearch(''); }
    else navigate('/', { replace: true });
  }, [stateId, states]); // eslint-disable-line

  useEffect(() => {
    if (!circuitId || states.length === 0) return;
    const c = CIRCUITS.find(ci => ci.id === circuitId);
    if (c) { loadCircuit(c, states); setSearch(''); }
    else navigate('/', { replace: true });
  }, [circuitId, states]); // eslint-disable-line

  // Handlers — navigate to URL
  const handleHomeSelect    = () => { navigate('/'); clearState(); clearCircuit(); closeSidebar(); };
  const handleStateSelect = s => {
    const p = new URLSearchParams(searchParams);
    p.delete('city');
    const qs = p.toString();
    navigate('/state/' + s.id + (qs ? '?' + qs : ''));
    closeSidebar();
  };
  const handleCircuitSelect = c  => { navigate('/circuit/' + c.id); closeSidebar(); };
  const handleCircuitClear  = () => { navigate('/'); closeSidebar(); };

  const updateParam = (key, val, def) => {
    const p = new URLSearchParams(searchParams);
    if (val === def) p.delete(key); else p.set(key, val);
    setSearchParams(p, { replace: true });
  };

  const handleFaithToggle = (faith) => {
    const next = new Set(faithSet);
    if (next.has(faith)) { if (next.size > 1) next.delete(faith); }
    else next.add(faith);
    const p = new URLSearchParams(searchParams);
    const arr = [...next];
    if (arr.length === 3) p.delete('faith'); else p.set('faith', arr.join(','));
    if (!(arr.length === 1 && arr[0] === 'hindu')) p.delete('deity');
    setSearchParams(p, { replace: true });
  };

  const handleCitySelect  = city  => updateParam('city',  city,  'all');
  const handleDeitySelect = deity => updateParam('deity', deity, 'all');
  const handleViewChange  = v     => updateParam('view',  v,     'grid');

  // Derived data
  const filterMode  = 'district';
  const filtered    = useMemo(
    () => filterTemples(stateData, { faithSet, filterValue: activeCity, filterField: filterMode, search, deity: activeDeity }),
    [stateData, faithSet, activeCity, filterMode, search, activeDeity]
  );
  const filterItems = useMemo(() => {
    const base = stateData ? stateData.filter(t => faithSet.has(t.faith)) : [];
    return filterMode === 'district' ? getDistricts(base) : getCities(base);
  }, [stateData, faithSet, filterMode]);
  const stats      = useMemo(() => getStats(filtered),   [filtered]);
  const totalStats = useMemo(() => getStats(stateData),  [stateData]);
  const stateLabel = activeState ? (lang === 'en' ? activeState.name_en : activeState.name_hi) : '';

  const displayTemples = activeCircuit ? circuitTemples : filtered;
  const displayLoading = activeCircuit ? circuitLoading  : loading;
  const displayError   = activeCircuit ? circuitError    : error;

  const isMapMode    = view === 'map';
  const isNearbyMode = view === 'nearby';

  return (
    <div className="app-shell">
      <Header lang={lang} onLangToggle={setLang} view={view} onViewChange={handleViewChange}
        sidebarOpen={sidebarOpen} onSidebarToggle={() => setSidebarOpen(o => !o)} />
      <div className="app-body">
        <div className={`sidebar-overlay${sidebarOpen ? ' sidebar-open' : ''}`} onClick={closeSidebar} />
        <Sidebar
          states={states} activeStateId={activeState?.id} onStateSelect={handleStateSelect}
          isHome={isHome} onHomeSelect={handleHomeSelect}
          activeCircuit={activeCircuit} onCircuitSelect={handleCircuitSelect} onCircuitClear={handleCircuitClear}
          faithSet={faithSet} onFaithToggle={handleFaithToggle}
          isHinduOnly={isHinduOnly} activeDeity={activeDeity} onDeitySelect={handleDeitySelect}
          filterItems={filterItems} filterMode={filterMode} activeCity={activeCity} onCitySelect={handleCitySelect}
          stats={totalStats} lang={lang} isOpen={sidebarOpen} onClose={closeSidebar}
        />
        <main className={`main-content${isMapMode ? ' map-mode' : ''}`}>
          {isNearbyMode ? (
            <NearbyView lang={lang} />
          ) : isMapMode ? (
            <MapView
              temples={displayTemples || []}
              lang={lang}
              loading={displayLoading}
              error={displayError}
            />
          ) : isHome ? (
            <>
              <IndiaMap states={states} onStateSelect={handleStateSelect} lang={lang} faithSet={faithSet} />
              <SacredMarkersInfo lang={lang} />
              <TempleOfDay lang={lang} />
            </>
          ) : (
            <>
              {activeCircuit && !circuitLoading && (
                <div className="circuit-banner" style={{ '--cb-color': activeCircuit.color, '--cb-bg': activeCircuit.bgColor, '--cb-border': activeCircuit.borderColor }}>
                  <span className="circuit-banner-icon">{activeCircuit.icon}</span>
                  <div className="circuit-banner-text">
                    <h2 className="circuit-banner-name">{lang === 'en' ? activeCircuit.name_en : activeCircuit.name_hi}</h2>
                    <p className="circuit-banner-desc">{lang === 'en' ? activeCircuit.desc_en : activeCircuit.desc_hi}</p>
                  </div>
                  {circuitTemples && (
                    <span className="circuit-banner-count">{circuitTemples.length} {lang === 'en' ? 'temples' : 'मंदिर'}</span>
                  )}
                </div>
              )}
              {activeState && !loading && !activeCircuit && (
                <>
                  <StatsBar stateName={stateLabel} stats={stats} lang={lang} />
                  <SearchBar value={search} onChange={setSearch} lang={lang}
                    resultCount={filtered.length} totalCount={stateData?.length ?? 0} />
                </>
              )}
              <TempleGrid
                temples={displayTemples} loading={displayLoading} error={displayError}
                lang={lang} activeState={activeState} circuitMode={!!activeCircuit}
              />
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/"                     element={<AppShell />} />
      <Route path="/state/:stateId"       element={<AppShell />} />
      <Route path="/circuit/:circuitId"   element={<AppShell />} />
      <Route path="*"                     element={<Navigate to="/" replace />} />
    </Routes>
  );
}
